import _ from '@luna/lodash';
import { createEntity, request } from '@luna/api';
import { deepClone } from '@luna/utils';
import { getEnv } from '@luna/env';

import { getUserConfigurationStrategy } from './UserConfigurationSaveStrategy';
import type { UserConfiguration, UserConfigurationEntity } from './UserConfigurationTypes';

type DeprecatedUserConfigurationEntityWithId = UserConfigurationEntity & { id: string };

const defaultGlobalUserConfiguration: UserConfiguration = {
    frontend: {
        bundleInfoShown: false,
        firstVisit: {},
        migratedToNewDashboard: false,
        overviewMode: 'cards',
        redTransactions: false,
        setupShown: false,
        sIDAvailableNotificationShown: false,
        soundEnabled: false,
    },
    overview: {},
    todoService: {},
};

let getSelectUserConfigurationId: (
    id: string,
) => DeprecatedUserConfigurationEntityWithId | undefined;

let updateUserConfigurationCache: (
    entity: DeprecatedUserConfigurationEntityWithId,
) => void | undefined;

/** @deprecated - Do not use the configuration as a key value store, each squad should implement their own solution */
export const {
    useEntity: useDeprecatedUserConfiguration,
    useUpdateEntity: useDeprecatedUpdateUserConfiguration,
    queryAll: queryDeprecatedUserConfigurations,
    queryById: queryDeprecatedUserConfiguration,
    queryByIdIfNeeded: queryDeprecatedUserConfigurationIfNeeded,
    updateById: updateDeprecatedUserConfiguration,
    selectById: selectDeprecatedUserConfigurationById,
    updateCache,
} = createEntity({
    name: 'userConfiguration',
    withEntityType: {} as DeprecatedUserConfigurationEntityWithId,
    payloadCreators: {
        queryAll: async (options?: unknown) => {
            const globalUserConfiguration = await request<UserConfiguration>(
                `${getEnv().georgeApiUrl}my/configuration`,
            );
            const globalUserConfigurationWithDefaults = _.merge(
                {},
                defaultGlobalUserConfiguration,
                globalUserConfiguration,
            );

            getUserConfigurationStrategy()?.saveAll(
                deepClone(globalUserConfigurationWithDefaults),
                options,
            );

            return Object.entries(
                globalUserConfigurationWithDefaults as unknown as Record<
                    string,
                    UserConfigurationEntity
                >,
            ).map(
                ([id, userConfiguration]): DeprecatedUserConfigurationEntityWithId => ({
                    id: id!,
                    ...userConfiguration,
                }),
            );
        },
        queryById: async (id: string) => {
            const userConfiguration = await request<UserConfiguration[keyof UserConfiguration]>(
                `${getEnv().georgeApiUrl}my/configuration/${id}`,
            );

            getUserConfigurationStrategy()?.saveOne(id, deepClone(userConfiguration));

            return {
                id,
                ...userConfiguration,
            };
        },
        updateById: async ({ id, changes, options }) => {
            const original = getSelectUserConfigurationId(id);

            const updatedUserConfiguration = {
                ...original,
                ...changes,
            } as UserConfiguration[keyof UserConfiguration];

            // update cache in case of concurrent updates
            updateUserConfigurationCache(
                updatedUserConfiguration as DeprecatedUserConfigurationEntityWithId,
            );

            await request.post<UserConfiguration[keyof UserConfiguration]>(
                `${getEnv().georgeApiUrl}my/configuration/${id}`,
                updatedUserConfiguration,
            );

            getUserConfigurationStrategy()?.saveOne(
                id,
                deepClone(updatedUserConfiguration),
                options,
            );

            return {
                id,
                ...updatedUserConfiguration,
            };
        },
    },
});

getSelectUserConfigurationId = selectDeprecatedUserConfigurationById;
updateUserConfigurationCache = updateCache;
